import {
  addDays,
  HOLD_REVIEW_MAX_DAYS,
  type IssueDetail,
  type IssuePriority,
  type IssueRejectReason,
  REJECT_OTHER_NOTE_MIN_LENGTH,
} from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMembers, useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { useIssueCases } from '../../hooks/useIssueTodo.js';
import {
  formatShortDate,
  holdPresetDate,
  ISSUE_PRIORITY_LABEL_KEYS,
  ISSUE_REJECT_REASON_LABEL_KEYS,
  jstToday,
} from '../../lib/issue-mgmt-format.js';
import { ChoiceChips } from './ChoiceChips.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 小窓で選べる判定。
 * @graph-connects none
 */
export type DecisionChoice = 'accepted' | 'on_hold' | 'rejected';

/**
 * 見直す日の候補の札（2週間後・30日後・60日後の日数と札の値）。
 * @graph-connects none
 */
const HOLD_PRESETS = [
  { days: 14, choice: '14' },
  { days: 30, choice: '30' },
  { days: 60, choice: '60' },
] as const;

/**
 * 保留の見直す日の選び方（何日後か、日付を選ぶか）。
 * @graph-connects none
 */
type HoldChoice = (typeof HOLD_PRESETS)[number]['choice'] | 'pick';

/**
 * 優先度の並び。
 * @graph-connects none
 */
const PRIORITIES: readonly IssuePriority[] = ['high', 'medium', 'low'];

/**
 * 「対応しない」の理由の並び。
 * @graph-connects none
 */
const REJECT_REASONS: readonly IssueRejectReason[] = [
  'as_designed',
  'cost_outweighs',
  'not_reproducible',
  'policy',
  'resolved_elsewhere',
  'other',
];

/**
 * 補足の上限字数（判定の API と同じ）。
 * @graph-connects none
 */
const NOTE_MAX_LENGTH = 500;

/**
 * 判定の小窓（対応する・保留・対応しない）。今の値が選ばれた状態で開く。
 *
 * 対応するは担当・優先度・開発するかの3つ、保留は見直す日（翌日〜180日後）、対応しないは理由（その他なら補足10字以上）が
 * 揃うまで「決定」を押せない。足りないものは左下に書く。未完了の案件がある「対応する」を保留・対応しないへ変えるときは
 * 補足が要る（未完了の数は案件欄と同じく写しから数える。読み終えるまでは決定できない）。
 * 案件が紐づいている間は開発を「不要」にできない（BR-27）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題を対応する・保留・対応しないに判定する小窓。必須項目が揃うまで決定できず、決定したらトーストで知らせて元に戻せるようにする
 * @graph-connects useIssueMembers [calls] 担当の候補
 * @graph-connects useIssueMgmtWrite [calls] decide
 * @graph-connects useIssueCases [calls] 未完了の案件の数
 * @graph-connects useIssueToast [calls] 保存の知らせ
 */
export function DecideDialog({
  issue,
  initialDecision,
  onClose,
  onDecided,
  now = new Date(),
}: {
  issue: IssueDetail;
  initialDecision: DecisionChoice;
  onClose: () => void;
  /** 決定した後に呼ぶ（やることのカードを済みにするため）。label は結果の1行 */
  onDecided?: (label: string, operationId: string | null) => void;
  now?: Date;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const members = useIssueMembers();
  const decide = useIssueMgmtWrite('decide');
  const today = jstToday(now);
  const tomorrow = addDays(today, 1);
  const latest = addDays(today, HOLD_REVIEW_MAX_DAYS);
  const caseCount = issue.caseLinks.length;
  const devLocked = issue.devRequired === true && caseCount > 0;
  const leavingAcceptedWithCases = issue.verdict === 'accepted' && caseCount > 0;
  const cases = useIssueCases(issue.id, leavingAcceptedWithCases);
  const incomplete = leavingAcceptedWithCases ? (cases.data?.incomplete ?? null) : 0;

  const [decision, setDecision] = useState<DecisionChoice>(initialDecision);
  const [assignee, setAssignee] = useState<string | null>(
    issue.assignee?.hasEditKey === true ? issue.assignee.email : null,
  );
  const [priority, setPriority] = useState<IssuePriority | null>(issue.priority);
  const [devRequired, setDevRequired] = useState<boolean | null>(issue.devRequired);
  const [holdChoice, setHoldChoice] = useState<HoldChoice>('30');
  const [pickedDate, setPickedDate] = useState(issue.holdUntil ?? '');
  const [rejectReason, setRejectReason] = useState<IssueRejectReason | null>(issue.rejectReason);
  const [note, setNote] = useState(issue.verdictNote ?? '');

  const holdUntil = holdChoice === 'pick' ? pickedDate : holdPresetDate(now, Number(holdChoice));
  const noteLength = Array.from(note.trim()).length;
  const leaving = issue.verdict === 'accepted' && decision !== 'accepted';
  const needsCaseNote = leaving && incomplete !== null && incomplete > 0;

  const missingHint = (() => {
    if (decision === 'accepted') {
      return assignee === null || priority === null || devRequired === null
        ? t('issueMgmt.decide.hintAccepted')
        : null;
    }
    if (decision === 'on_hold' && (holdUntil < tomorrow || holdUntil > latest)) {
      return t('issueMgmt.decide.hintHold');
    }
    if (decision === 'rejected' && rejectReason === null) return t('issueMgmt.decide.hintReject');
    if (
      decision === 'rejected' &&
      rejectReason === 'other' &&
      noteLength < REJECT_OTHER_NOTE_MIN_LENGTH
    ) {
      return t('issueMgmt.decide.hintOther');
    }
    if (leaving && incomplete === null) return t('issueMgmt.decide.hintCasesLoading');
    if (needsCaseNote && noteLength === 0) return t('issueMgmt.decide.hintCaseNote');
    return null;
  })();

  const showNote = decision === 'rejected' || needsCaseNote;
  const memberList = members.data?.members ?? [];

  /** 決定した内容の1行（やることのカードの結果に出す）。決定できる状態（必須項目が揃っている）で呼ぶ。 */
  const decisionSummary = (): string => {
    if (decision === 'accepted' && assignee !== null && priority !== null) {
      return t('issueMgmt.decide.summaryAccepted', {
        name: memberList.find((m) => m.email === assignee)?.displayName ?? assignee,
        priority: t(ISSUE_PRIORITY_LABEL_KEYS[priority]),
      });
    }
    if (decision === 'on_hold') {
      return t('issueMgmt.decide.summaryHold', { date: formatShortDate(holdUntil, now) });
    }
    return rejectReason === null
      ? t('issueMgmt.verdict.rejected')
      : t('issueMgmt.decide.summaryRejected', {
          reason: t(ISSUE_REJECT_REASON_LABEL_KEYS[rejectReason]),
        });
  };

  const submit = () => {
    const trimmed = note.trim();
    decide.mutate(
      {
        issueId: issue.id,
        decision,
        ...(decision === 'accepted' &&
        assignee !== null &&
        priority !== null &&
        devRequired !== null
          ? { assignee, priority, devRequired }
          : {}),
        ...(decision === 'on_hold' ? { holdUntil } : {}),
        ...(decision === 'rejected' && rejectReason !== null ? { rejectReason } : {}),
        // 補足欄を出していないときは送らない（開いた時点の補足を、別の判定へ黙って引き継がない）
        ...(showNote && trimmed !== '' ? { note: trimmed } : {}),
        base: {
          verdict: issue.verdict,
          assignee: issue.assignee?.email ?? null,
          priority: issue.priority,
          devRequired: issue.devRequired,
          holdUntil: issue.holdUntil,
          rejectReason: issue.rejectReason,
          verdictNote: issue.verdictNote,
        },
      },
      {
        onSuccess: (result) => {
          onClose();
          const summary = decisionSummary();
          onDecided?.(summary, result.operationId);
          if (result.operationId === null) {
            toast.notifySaved(t('issueMgmt.common.unchanged'));
            return;
          }
          const details = [
            ...(decision === 'on_hold'
              ? [t('issueMgmt.decide.savedHoldDetail', { date: formatShortDate(holdUntil, now) })]
              : []),
            ...(result.maskedFields.length > 0 ? [t('issueMgmt.form.masked')] : []),
          ];
          const message =
            decision === 'accepted'
              ? t('issueMgmt.decide.savedAccepted', { title: issue.title })
              : decision === 'on_hold'
                ? t('issueMgmt.decide.savedHold', { title: issue.title })
                : t('issueMgmt.decide.savedRejected', { title: issue.title });
          toast.notifySaved(message, {
            operationId: result.operationId,
            ...(details.length > 0 ? { detail: details.join(' ／ ') } : {}),
          });
        },
        onError: toast.notifyError,
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={t('issueMgmt.decide.title', { title: issue.title })}
      description={issue.id}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-[var(--color-text-muted)]">{missingHint ?? ''}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {t('issueMgmt.common.cancel')}
            </Button>
            <Button disabled={missingHint !== null} loading={decide.isPending} onClick={submit}>
              {t('issueMgmt.common.decide')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <ChoiceChips<DecisionChoice>
          label={t('issueMgmt.decide.decision')}
          value={decision}
          onChange={setDecision}
          choices={[
            { value: 'accepted', label: t('issueMgmt.verdict.accepted') },
            { value: 'on_hold', label: t('issueMgmt.verdict.onHold') },
            { value: 'rejected', label: t('issueMgmt.verdict.rejected') },
          ]}
        />

        {decision === 'accepted' && (
          <>
            <Field label={t('issueMgmt.decide.assignee')}>
              {memberList.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t('issueMgmt.decide.noMembers')}
                </p>
              ) : (
                <ChoiceChips<string>
                  label={t('issueMgmt.decide.assignee')}
                  value={assignee}
                  onChange={setAssignee}
                  choices={memberList.map((m) => ({ value: m.email, label: m.displayName }))}
                />
              )}
            </Field>
            <Field label={t('issueMgmt.decide.priority')}>
              <ChoiceChips<IssuePriority>
                label={t('issueMgmt.decide.priority')}
                value={priority}
                onChange={setPriority}
                choices={PRIORITIES.map((value) => ({
                  value,
                  label: t(ISSUE_PRIORITY_LABEL_KEYS[value]),
                }))}
              />
            </Field>
            <Field label={t('issueMgmt.decide.devRequired')}>
              <ChoiceChips<boolean>
                label={t('issueMgmt.decide.devRequired')}
                value={devRequired}
                onChange={setDevRequired}
                choices={[
                  { value: true, label: t('issueMgmt.detail.devYes') },
                  {
                    value: false,
                    label: t('issueMgmt.detail.devNo'),
                    disabled: devLocked,
                    ...(devLocked ? { hint: t('issueMgmt.decide.devLocked') } : {}),
                  },
                ]}
              />
            </Field>
          </>
        )}

        {decision === 'on_hold' && (
          <Field label={t('issueMgmt.decide.holdWhen')}>
            <ChoiceChips<HoldChoice>
              label={t('issueMgmt.decide.holdWhen')}
              value={holdChoice}
              onChange={setHoldChoice}
              choices={[
                ...HOLD_PRESETS.map(({ days, choice }) => ({
                  value: choice,
                  label: t('issueMgmt.decide.holdPreset', {
                    days: String(days),
                    date: formatShortDate(holdPresetDate(now, days), now),
                  }),
                })),
                { value: 'pick', label: t('issueMgmt.decide.holdPick') },
              ]}
            />
            {holdChoice === 'pick' && (
              <input
                type="date"
                aria-label={t('issueMgmt.decide.holdDate')}
                value={pickedDate}
                min={tomorrow}
                max={latest}
                onChange={(e) => {
                  setPickedDate(e.target.value);
                }}
                className="mt-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
              />
            )}
          </Field>
        )}

        {decision === 'rejected' && (
          <Field label={t('issueMgmt.decide.reason')}>
            <ChoiceChips<IssueRejectReason>
              label={t('issueMgmt.decide.reason')}
              value={rejectReason}
              onChange={setRejectReason}
              choices={REJECT_REASONS.map((value) => ({
                value,
                label: t(ISSUE_REJECT_REASON_LABEL_KEYS[value]),
              }))}
            />
          </Field>
        )}

        {needsCaseNote && (
          <p className="rounded-lg bg-[color-mix(in_srgb,var(--color-warning)_12%,transparent)] px-3 py-2 text-sm text-[var(--color-text-primary)]">
            {t('issueMgmt.decide.caseNote', { n: String(incomplete) })}
          </p>
        )}

        {showNote && (
          <div>
            <label
              htmlFor="issue-decide-note"
              className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]"
            >
              {t('issueMgmt.decide.note')}
            </label>
            <Textarea
              id="issue-decide-note"
              rows={3}
              maxLength={NOTE_MAX_LENGTH}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * 小窓の1項目（見出しと中身）。
 * @graph-connects none
 */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-sm font-semibold text-[var(--color-text-primary)]">{label}</p>
      {children}
    </div>
  );
}
